import { connect } from 'react-redux';
import { push } from 'redux-first-history';
import MoveInCostsSchedulingPage from './component';
import { getRachelDashboard } from '@renter-hub/actions/requests';
import { closeNotification, showPageNotification } from '@common/actions/NotificationActions';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import {
  DEFAULT_ERROR,
  MOVE_IN_COSTS_PAID,
  MOVE_IN_COSTS_SCHEDULED,
  PAYMENT_METHOD_INSUFFICIENT_FUNDS,
  PAYMENT_UNPROCESSABLE,
} from '@renter-hub/constants/notifications';
import HTTP_STATUS from '@common/constants/HttpConstants';
import paths from '@renter-hub/pathsManifest';
import TransactionConstants from '@common/constants/TransactionConstants';
import ResponseMessageCode from '@common/constants/ResponseMessageCode';
import type { ErrorInterface } from '@common/types';
import { logError } from '@zg-rentals/log-error';
import type { ScheduleMoveInCostsWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';
import { ScheduleMoveInCostsWebApiHandler_scheduleMoveInCosts } from '@zg-rentals/ts-rent-payment-client';

const mapStateToProps = (state, { fromDashboard, location, failedPaymentEmail }) => ({
  failedPaymentEmail,
  moveInCostsDetails: state.payments.renter.dashboard.moveInCostsDetails,
  fromDashboard,
  encodedPaymentMethodId: state.payments.renter.dashboard?.selectedPaymentMethod?.id,
  newPaymentMethodId: location?.state?.paymentMethodId,
  user: state.user,
  paymentMethods: state.payments.renter.dashboard.paymentMethods,
  listingAlias: state.payments?.renter?.dashboard?.listingAlias,
});

const mapDispatchToProps = (dispatch, { baseUrl, brand, propertyId, fromDashboard }: any) => ({
  onAddNewPaymentClick: () => {
    return dispatch(
      push({
        pathname: baseUrl + paths.editPaymentInfoPage,
        state: {
          fromWhere: paths.editMoveInCostsSchedulePage,
        },
      }),
    );
  },
  onSubmit: async (moveInCostsPaymentInfo: ScheduleMoveInCostsWebApiRequestDto) => {
    try {
      // Close any open banners
      await dispatch(closeNotification());

      // Schedule move-in costs via Proxit API
      await ScheduleMoveInCostsWebApiHandler_scheduleMoveInCosts(moveInCostsPaymentInfo);

      // Request the latest renter status
      const [renterStatusResponse] = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      if (fromDashboard) {
        await dispatch(push(baseUrl));
      } else {
        await dispatch(push(baseUrl + paths.paymentSettingsPage));
      }

      // Show success notification
      const { moveInCostsDetails } = renterStatusResponse.payload.renterStatus || {};
      if (moveInCostsDetails.status === TransactionConstants.STATUS.COMPLETE) {
        await dispatch(showPageNotification(MOVE_IN_COSTS_PAID));
      } else {
        await dispatch(showPageNotification(MOVE_IN_COSTS_SCHEDULED));
      }

      return { error: false };
    } catch (err) {
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status, response } = error.action.payload;
        const { messageCode } = response || {};

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }

        // If 422, show insufficient funds or card declined notification
        if (status === HTTP_STATUS.SC_UNPROCESSABLE_ENTITY) {
          logError({ error: err, context: { status, messageCode } });
          if (messageCode === ResponseMessageCode.INSUFFICIENT_FUNDS) {
            await dispatch(showPageNotification(PAYMENT_METHOD_INSUFFICIENT_FUNDS));
          } else {
            await dispatch(showPageNotification(PAYMENT_UNPROCESSABLE));
          }
          await dispatch(push(baseUrl));
          return { error: true };
        }
      }

      logError({ error: err });
      dispatch(showPageNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  goToDashboard: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl));
  },
  goToPaymentSettingPage: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl + paths.paymentSettingsPage));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(MoveInCostsSchedulingPage);
